<template>
  <a-modal
    width="800px"
    :visible="isVisible"
    :footer="null"
    @cancel="onCloseModal"
  >
    <h6>
      {{ modalTitle }}
    </h6>
    <a-spin :spinning="isLoading">
      <table class="table-mapping mt-4">
        <thead>
          <tr>
            <th style="width: 34%">
              {{ $t('store_list_page.mapping_modal.table.channel_warehouse') }}
            </th>
            <th style="width: 34%">
              {{ $t('store_list_page.mapping_modal.table.master_warehouse') }}
            </th>
            <th class="text-center" style="width: 12%">
              {{ $t('store_list_page.mapping_modal.table.status') }}
            </th>
            <th class="text-center" style="width: 20%">
              {{ $t('store_list_page.mapping_modal.table.action') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!channelWarehouseList || !channelWarehouseList.length">
            <td colspan="4" class="text-center">
              {{ $t('store_list_page.mapping_modal.table.no_data') }}
            </td>
          </tr>
          <tr v-for="(warehouse, index) in channelWarehouseList" :key="index">
            <td>{{ warehouse.name }}</td>
            <td>
              <template v-if="warehouse.edit">
                <a-select
                  v-model="warehouse.warehouse_id"
                  class="w-100"
                  show-search
                  :placeholder="$t('store_list_page.mapping_modal.table.select_master_placeholder')"
                  option-filter-prop="children"
                  :options="masterWarehouseList"
                  :filter-option="filterOption"
                  @change="onChangeWarehouseMaster($event, index)"
                />
              </template>
              <template v-else>
                <span v-if="warehouse.warehouse_master_name">
                  {{ warehouse.warehouse_master_name }}
                </span>
                <span v-else style="font-style: italic; color: #999">
                  {{ $t('store_list_page.mapping_modal.table.not_connected_label') }}
                </span>
              </template>
            </td>
            <td class="text-center">
              <a-tag :color="warehouse.connected ? 'green' : 'red'" class="w-100">
                {{ !warehouse.connected ? $t('store_list_page.mapping_modal.table.not_connected_tag') : $t('store_list_page.mapping_modal.table.connected_tag') }}
              </a-tag>
            </td>
            <td class="text-center">
              <template v-if="!warehouse.edit">
                <a-tooltip v-if="!warehouse.connected" placement="top">
                  <template slot="title">
                    <span>{{ $t('store_list_page.mapping_modal.tooltips.add_sync') }}</span>
                  </template>
                  <a-button :disabled="isLoading" @click="warehouse.edit = true">
                    <a-icon type="plus" />
                  </a-button>
                </a-tooltip>
                <a-tooltip v-else placement="top">
                  <template slot="title">
                    <span>{{ $t('store_list_page.mapping_modal.tooltips.disconnect_sync') }}</span>
                  </template>
                  <a-popconfirm
                    placement="bottom"
                    :title="$t('store_list_page.mapping_modal.confirm.disconnect_title')"
                    description=""
                    :ok-text="$t('store_list_page.mapping_modal.confirm.disconnect_ok')"
                    :cancel-text="$t('store_list_page.mapping_modal.confirm.disconnect_cancel')"
                    @confirm="onAction(index, 'delete')"
                  >
                    <a-button :disabled="isLoading">
                      <a-icon type="disconnect" />
                    </a-button>
                  </a-popconfirm>
                </a-tooltip>
              </template>
              <template v-else>
                <a-tooltip placement="top">
                  <template slot="title">
                    <span>{{ $t('store_list_page.mapping_modal.tooltips.save_sync') }}</span>
                  </template>
                  <a-button :disabled="isLoading" @click="onAction(index, 'save')">
                    <a-icon type="check" />
                  </a-button>
                </a-tooltip>
                <a-tooltip placement="top">
                  <template slot="title">
                    <span>{{ $t('store_list_page.mapping_modal.tooltips.cancel') }}</span>
                  </template>
                  <a-button :disabled="isLoading" class="ml-3" @click="onCancelEdit(index)">
                    <a-icon type="close" />
                  </a-button>
                </a-tooltip>
              </template>
            </td>
          </tr>
        </tbody>
      </table>
    </a-spin>
  </a-modal>
</template>

<script>
import { getWarehouseList } from '@/api/warehouse'
import { getLazadaWarehouseChannelList, adjustLazadaMappingWarehouseChannel } from '@/api/channels/lazada'
import { getTokopediaWarehouseChannelList, adjustTokopediaMappingWarehouseChannel } from '@/api/channels/tokopedia'
import { getShopeeWarehouseChannelList, mappingWarehouseShopeeChannel, unMappingWarehouseShopeeChannel, checkShopeeProductSyncStatus } from '@/api/channels/shopee'
import { getTiktokWarehouseChannelList, adjusTiktoktMappingWarehouseChannel, mappingWarehouseTiktokChannel, unMappingWarehouseTiktokChannel } from '@/api/channels/tiktok'
import { getTocoWarehouseChannelList, mappingWarehouseTocoChannel, unMappingWarehouseTocoChannel } from '@/api/channels/toco'
import { disconnecMappingWarehouseChannel, connectMappingWarehouseChannel, updateMappingWarehouseChannel } from '@/api/channels/index'

import { sidebarChannelRefreshMixin } from '@/utils/mixins/sidebarChannelRefreshMixins'

const SHOPEE_WAREHOUSE_RETRY_DELAYS = [500, 1000, 1500, 2000, 2500]

export default {
  name: 'ModalMappingWarehouseAfterIntegrateV2',
  mixins: [sidebarChannelRefreshMixin],
  props: {
    data: {
      type: Object,
      default: () => {},
    },
    back: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      isVisible: false,
      isLoading: false,
      modalTitle: 'Mapping',
      masterWarehouseList: [],
      channelWarehouseList: [],
    }
  },
  computed: {
    businessId() {
      return this.$route.query.business_id || Object.keys(this.$store.getters['user/businessListObjectByKey'])[0]
    },
    channelId() {
      return this.$route.query.channel_id
    },
    channelType() {
      return this.$route.query.code
    },
    workspaceId() {
      return this.$route.query.workspace_id
    },
    channel() {
      return this.data
    },
    integration() {
      return this.$store.state.integration
    },
  },
  watch: {
    'integration.modalWarehouseMappingV2': {
      immediate: true,
      deep: true,
      handler: function (value) {
        this.isVisible = value
        this.isVisible = value
        this.modalTitle = this.$t('store_list_page.mapping_modal.title', { channelName: '', storeName: '' })
        this.modalTitle = this.$t('store_list_page.mapping_modal.title_main')
        if (value) {
          this.init()
        } else {
          this.masterWarehouseList = []
          this.channelWarehouseList = []
          this.isLoading = false
        }
      },
    },
  },
  methods: {
    async init() {
      this.isLoading = true
      await Promise.all([this.fetchWarehouseMaster(), this.fetchWarehouseChannel()])
        .then(([responseMaster, responseChannel]) => {
          this.masterWarehouseList = responseMaster.map(master => ({
            value: master.id,
            label: master.name,
          }))
          this.channelWarehouseList = responseChannel.map(channel => {
            const warehouseId = channel?.warehouse_id || channel?.warehouse_master_id || null
            const findMasterWarehouse = responseMaster.find(master => master.id === warehouseId)
            return {
              ...channel,
              warehouse_id: warehouseId,
              prev_warehouse_id: warehouseId,
              original_warehouse_id: warehouseId,
              warehouse_master_name: findMasterWarehouse?.name || '',
              connected: !!findMasterWarehouse?.id || !!warehouseId,
              edit: false,
            }
          })
        })
        .catch(() => {
          this.masterWarehouseList = []
          this.channelWarehouseList = []
        })
        .finally(() => this.isLoading = false)
    },
    async fetchWarehouseMaster() {
      return await getWarehouseList({ business_id: this.businessId })
        .then(({ data: { data: response } }) => response || [])
        .catch(() => [])
    },
    async fetchWarehouseChannel() {
      let promises = []
      const bodyReq = {
        business_id: this.businessId,
        channel_id: this.channelId,
        params: {
          check_sync: true,
        },
      }
      if (['tiktok', 'tiktok_id'].includes(this.channelType)) {
        promises.push(getTiktokWarehouseChannelList(bodyReq))
      } else if (['shopee_id'].includes(this.channelType)) {
        promises.push(getShopeeWarehouseChannelList(bodyReq))
      } else if (['toco_id'].includes(this.channelType)) {
        promises.push(getTocoWarehouseChannelList(bodyReq))
      }
      return await Promise.all(promises)
        .then(([{ data }]) => {
          if (['shopee_id', 'toco_id'].includes(this.channelType)) {
            const shopeeWarehouses = data?.data?.warehouses || data?.response || []
            if (!shopeeWarehouses.length) {
              return [{
                id: null,
                warehouse_channel_id: '',
                warehouse_master_id: null,
                name: 'Default Warehouse',
              }]
            }
            return shopeeWarehouses.map(item => ({
              id: item.id || null,
              warehouse_channel_id: item.id || item.channel_warehouse_id || item.locationId || '',
              warehouse_master_id: item.warehouse_master_id || null,
              warehouse_master_name: item.warehouse_master_name || '',
              name: item.warehouse_name || item.warehouseName || '',
            }))
          } else if (['tiktok', 'tiktok_id'].includes(this.channelType)) {
            return data?.data?.warehouse_list || []
          }
          return data?.data || []
        })
        .catch(() => [])
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0]?.text.toLowerCase().indexOf(input.toLowerCase()) >= 0
      )
    },
    onChangeWarehouseMaster(value, idx) {
      let duplicateIndex = this.channelWarehouseList.findIndex((channel, index) => channel.warehouse_id === value && index != idx)
      if (duplicateIndex > -1) {
        if (this.channelWarehouseList[duplicateIndex].connected) {
          this.$message.error(this.$t('store_list_page.mapping_modal.messages.warehouse_already_connected'))
          duplicateIndex = idx
        }
        this.channelWarehouseList[duplicateIndex].warehouse_id = this.channelWarehouseList[duplicateIndex].prev_warehouse_id
      } else {
        this.channelWarehouseList[idx].prev_warehouse_id = value
      }
    },
    onAction(index, action) {
      if (!this.channelWarehouseList[index].warehouse_id) {
        return this.$message.error(this.$t('store_list_page.mapping_modal.messages.master_warehouse_not_selected'))
      }
      if (this.channelType === 'shopee_id') {
        if (action === 'save') {
          this.onConnectShopee(index)
        } else {
          this.onDisconnectShopee(index)
        }
      } else if (this.channelType === 'toco_id') {
        if (action === 'save') {
          this.onConnectToco(index)
        } else {
          this.onDisconnectToco(index)
        }
      } else if (['tiktok', 'tiktok_id'].includes(this.channelType)) {
        this.onActionTiktok(index, action)
      } else {
        this.$message.info(this.$t('store_list_page.mapping_modal.messages.coming_soon'))
      }
    },
    async onConnectShopee(index) {
      this.isLoading = true
      const { warehouse_id, warehouse_channel_id, name: warehouse_name } = this.channelWarehouseList[index]
      const bodyReq = {
        business_id: this.businessId,
        data: {
          channel: { id: this.channelId, code: this.channelType, name: "Shopee Indonesia" },
          warehouse_id,
          warehouse_channel: {
            id: warehouse_channel_id,
            name: warehouse_name,
          },
        },
      }

      try {
        const response = await mappingWarehouseShopeeChannel(bodyReq)
        const isCompleted = await this.waitForShopeeWarehouseSync(response?.data?.request_id, this.channelId, 'MAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_add_sync') })
        this.$emit('onRefreshList')
        this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async onDisconnectShopee(index) {
      this.isLoading = true
      const { warehouse_channel_id } = this.channelWarehouseList[index]
      try {
        const response = await unMappingWarehouseShopeeChannel({
          business_id: this.businessId,
          data: {
            channel_id: this.channelId,
            channel_warehouse_id: warehouse_channel_id,
          },
        })
        const isCompleted = await this.waitForShopeeWarehouseSync(response?.data?.request_id, this.channelId, 'UNMAP_WAREHOUSE')
        if (!isCompleted) {
          this.isLoading = false
          return
        }
        await this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect_sync') })
        this.$emit('onRefreshList')
        this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
      } catch (err) {
        console.error(err)
        this.isLoading = false
      }
    },
    async waitForShopeeWarehouseSync(requestId, channelId, mode) {
      if (!requestId) {
        throw new Error('Shopee warehouse mapping response has no request_id')
      }

      for (let attempt = 0; this.isVisible; attempt += 1) {
        let response
        try {
          response = await checkShopeeProductSyncStatus({
            business_id: this.businessId,
            workspace_id: this.workspaceId,
            channel_id: channelId,
            request_id: requestId,
          })
        } catch (err) {
          console.error(err)
          return true
        }
        if (response?.data?.status?.toUpperCase() === 'ERROR') return true

        const syncData = response?.data?.data?.find(item => item.request_id === requestId && item.mode === mode)
        const status = syncData?.status?.toUpperCase()

        if (status === 'COMPLETED') return true
        if (status === 'FAILED') {
          throw new Error(syncData?.message || `Shopee warehouse ${mode} failed`)
        }
        if (!['RUNNING', 'PENDING'].includes(status)) return false

        const retryDelay = SHOPEE_WAREHOUSE_RETRY_DELAYS[Math.min(attempt, SHOPEE_WAREHOUSE_RETRY_DELAYS.length - 1)]
        await new Promise(resolve => setTimeout(resolve, retryDelay))
      }
      return false
    },
    async onConnectToco(index) {
      this.isLoading = true
      const { warehouse_id, warehouse_channel_id, name: warehouse_name } = this.channelWarehouseList[index]
      const bodyReq = {
        business_id: this.businessId,
        data: {
          channel: { id: this.channelId, code: this.channelType, name: "Toco Indonesia" },
          warehouse_id,
          warehouse_channel: {
            id: warehouse_channel_id,
            name: warehouse_name,
          },
        },
      }

      await mappingWarehouseTocoChannel(bodyReq)
        .then(() => {
          this.init()
          this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_add_sync') })
          this.$emit('onRefreshList')
          this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
    },
    async onDisconnectToco(index) {
      this.isLoading = true
      const { warehouse_channel_id } = this.channelWarehouseList[index]
      await unMappingWarehouseTocoChannel({
        business_id: this.businessId,
        data: {
          channel_id: this.channelId,
          channel_warehouse_id: warehouse_channel_id,
        },
      })
      .then(() => {
        this.init()
        this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect_sync') })
        this.$emit('onRefreshList')
        this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
      })
      .catch((err) => {
        console.error(err)
        this.isLoading = false
      })
    },
    async onActionTiktok(idx, action) {
      const selectedWarehouseByIndex = this.channelWarehouseList[idx]
      if (!selectedWarehouseByIndex) {
        return this.$notification.error({
          message: this.$t('store_list_page.mapping_modal.messages.failed_sync_warehouse_not_found'),
        })
      }
      this.isLoading = true
      if (action === 'delete') {
        unMappingWarehouseTiktokChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            channel_id:  this.channelId,
          },
        })
        .then(() => {
          this.init()
          setTimeout(() => {
            this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_disconnect_sync') })
            this.$emit('onRefreshList')
            this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
          }, 2000)
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      } else {
        mappingWarehouseTiktokChannel({
          business_id: this.businessId,
          data: {
            channel_warehouse_id: selectedWarehouseByIndex.channel_warehouse_id,
            warehouse_id: selectedWarehouseByIndex.warehouse_id,
            warehouse_name: selectedWarehouseByIndex.name,
            auto_accept: selectedWarehouseByIndex.auto_accept,
            channel_order_auto_accept_step: selectedWarehouseByIndex.channel_order_auto_accept_step,
            channel_id: this.channelId,
          },
        })
        .then(() => {
          this.init()
          setTimeout(() => {
            this.$notification.success({ message: this.$t('store_list_page.mapping_modal.messages.success_sync')  })
            this.$emit('onRefreshList')
            this.notifySidebarChannelRefresh({ channelId: this.channelId, channelCode: this.channelType })
          }, 2000)
        })
        .catch((err) => {
          console.error(err)
          this.isLoading = false
        })
      }
    },
    onCancelEdit(index) {
      this.channelWarehouseList[index].warehouse_id = this.channelWarehouseList[index].original_warehouse_id
      this.channelWarehouseList[index].edit = false
    },
    onCloseModal() {
      this.$store.commit('integration/SET_MODAL_WAREHOUSE_MAPPING_V2', false)
    },
  },
}
</script>

<style lang="scss">
.table-mapping {
  width: 100%;

  tbody tr:hover td {
    background-color: #e1e5e9;
  }

  th {
    background-color: #f0f2f4;
    font-weight: 500;
  }

  th, td {
    padding: 11px;
    border-bottom: 1px solid #e4e9f0;
  }
}
</style>
